import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { googleConsentApi } from 'src/app/helpers/gtm.helper';
import { isLinkDomainUrl } from 'src/app/helpers/link-domain.helper';
import { DEFAULT_LANGUAGE, LanguageService } from 'src/app/services/language.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { REQ_URL } from 'src/app/services/tokens/req-url';
import { cookieConsentApi } from './cookie-consent.api';
import {
  COOKIE_CATEGORY_ADVERTISEMENT,
  COOKIE_CATEGORY_ANALYTICS,
  COOKIE_CATEGORY_NECESSARY,
  COOKIE_CONSENT_LOCALES,
  COOKIE_CONSENT_REVISION,
} from './cookie-consent.constants';
import { communityCodeFromUrl, CookieConsentI18n, mapCookieConsentTranslation } from './cookie-consent.mapper';

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly isBrowser: boolean;
  private initialized = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private languageService: LanguageService,
    private themeService: ThemeService,
    private translate: TranslateService,
    private router: Router,
    @Optional() @Inject(REQ_URL) private reqUrl: string,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  async init(): Promise<void> {
    if (!this.isBrowser || this.initialized) {
      return;
    }

    this.initialized = true;

    const isIframe = await this.themeService.getIsIframe();
    const isLinkDomain = isLinkDomainUrl(this.reqUrl || window.location.href);
    const defaultLanguage = this.translate.currentLang || DEFAULT_LANGUAGE;

    await cookieConsentApi.run({
      autoShow: !isIframe && !isLinkDomain,
      mode: 'opt-in',
      hideFromBots: true,
      revision: COOKIE_CONSENT_REVISION,
      disablePageInteraction: false,
      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'bottom left',
          equalWeightButtons: false,
          flipButtons: true,
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: false,
          flipButtons: false,
        },
      },
      categories: {
        [COOKIE_CATEGORY_NECESSARY]: {
          enabled: true,
          readOnly: true,
        },
        [COOKIE_CATEGORY_ANALYTICS]: {
          autoClear: {
            cookies: [
              { name: /^_ga/ },
              { name: '_gid' },
              { name: '_gat' },
            ],
          },
        },
        [COOKIE_CATEGORY_ADVERTISEMENT]: {
          autoClear: {
            cookies: [
              { name: /^_gcl_/ },
              { name: /^_gac/ },
            ],
          },
        },
      },
      language: {
        default: defaultLanguage,
        translations: Object.fromEntries(
          COOKIE_CONSENT_LOCALES.map(locale => [
            locale,
            () => this.buildTranslation(locale),
          ])
        ),
      },
      onConsent: () => this.syncGoogleConsent(),
      onChange: () => this.syncGoogleConsent(),
    });

    this.languageService.getCurrentLang().subscribe(lang => {
      if (lang) {
        void cookieConsentApi.setLanguage(lang, true);
      }
    });
  }

  showPreferences(): void {
    if (!this.isBrowser || !this.initialized) {
      return;
    }

    cookieConsentApi.showPreferences();
  }

  private syncGoogleConsent(): void {
    googleConsentApi.update({
      analytics: cookieConsentApi.acceptedCategory(COOKIE_CATEGORY_ANALYTICS),
      advertisement: cookieConsentApi.acceptedCategory(COOKIE_CATEGORY_ADVERTISEMENT),
    });
  }

  private async buildTranslation(locale: string): Promise<ReturnType<typeof mapCookieConsentTranslation>> {
    const table = await firstValueFrom(this.translate.getTranslation(locale));
    const texts = (table?.COOKIE_CONSENT ?? {}) as Partial<CookieConsentI18n>;
    return mapCookieConsentTranslation(texts, communityCodeFromUrl(this.router.url));
  }
}
